import * as React from "react";
import { Box, CPText, Link } from "../../../shared/components/base";
import { useNavigate, useLocation } from "react-router-dom";
import "./ShortCodePageStyles.scss";

import {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  StackLayout,
} from "@progress/kendo-react-layout";
import CPButton from "shared/components/shared/CPButton/CPButton";
import logo from "../../../assets/evans-bi-logo2.png";
import {
  useRequestPasswordlessCodeMutation,
  useVerifyPasswordlessCodeMutation,
} from "data/auth/auth";
import { storeCookie } from "shared/helpers";
import { accessTokenName } from "shared/config";
import dayjs from "dayjs";

const RESEND_COOLDOWN = 30;

const ShortCodePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const email: string = (location.state as { email?: string })?.email ?? "";

  const [digits, setDigits] = React.useState<string[]>(Array(6).fill(""));
  const inputRefs = React.useRef<(HTMLInputElement | null)[]>(
    Array(6).fill(null),
  );
  const code = digits.join("");
  const [codeError, setCodeError] = React.useState<string | null>(null);
  const [resendTimer, setResendTimer] = React.useState(RESEND_COOLDOWN);
  const [resendSuccess, setResendSuccess] = React.useState(false);

  const { mutate: verifyCode, isLoading: isVerifying } =
    useVerifyPasswordlessCodeMutation();
  const { mutate: requestCode, isLoading: isResending } =
    useRequestPasswordlessCodeMutation();

  // Redirect to login if email is missing
  React.useEffect(() => {
    if (!email) {
      navigate("/", { replace: true });
    }
  }, [email, navigate]);

  // Resend countdown
  React.useEffect(() => {
    if (resendTimer <= 0) return;

    const interval = setInterval(() => {
      setResendTimer((t) => {
        if (t <= 1) {
          clearInterval(interval);
          return 0;
        }
        return t - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [resendTimer]);

  const handleDigitChange = (index: number, value: string) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    const newDigits = [...digits];
    newDigits[index] = digit;
    setDigits(newDigits);
    setCodeError(null);
    if (digit && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleDigitKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === "Backspace") {
      if (digits[index]) {
        const newDigits = [...digits];
        newDigits[index] = "";
        setDigits(newDigits);
      } else if (index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    } else if (e.key === "Enter") {
      handleVerify();
    } else if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 6);
    if (!pasted) return;
    const newDigits = Array(6).fill("");
    for (let i = 0; i < 6; i++) {
      newDigits[i] = pasted[i] ?? "";
    }
    setDigits(newDigits);
    setCodeError(null);
    const nextEmpty = newDigits.findIndex((d) => !d);
    const focusIndex = nextEmpty === -1 ? 5 : nextEmpty;
    inputRefs.current[focusIndex]?.focus();
  };

  const handleVerify = () => {
    setCodeError(null);

    if (code.length < 6) {
      setCodeError("Please enter the code sent to your email.");
      return;
    }

    verifyCode(
      { email, code: code.trim() },
      {
        onSuccess: (data) => {
          if (data?.token) {
            const expires = data?.expires ?? dayjs().add(4, "hour").toDate().toUTCString();
            storeCookie(accessTokenName, data.token, expires);
          }
          window.location.href = "/";
        },
        onError: (err: unknown) => {
          const errorCode = (err as Record<string, string>)?.error;

          if (errorCode === "invalid_code") {
            setCodeError(
              "The code is incorrect or has expired. Please try again or request a new code.",
            );
          } else {
            setCodeError("An error occurred. Please try again.");
          }
        },
      },
    );
  };

  const handleResend = () => {
    setResendSuccess(false);
    setCodeError(null);

    requestCode(
      { email },
      {
        onSuccess: () => {
          setDigits(Array(6).fill(""));
          setResendTimer(RESEND_COOLDOWN);
          setResendSuccess(true);
        },
        onError: () => {
          setCodeError("Failed to resend the code. Please try again.");
        },
      },
    );
  };

  return (
    <Card className="login-form-card short-code-card">
      <CardHeader>
        <Box
          alignItems="center"
          justifyContent="center"
          className="logo-container"
        >
          <img
            src={logo}
            alt="BI Hub Logo"
            className="login-logo"
            width="300"
          />
        </Box>
      </CardHeader>
      <CardBody>
        <StackLayout orientation="vertical" gap={16}>
          <StackLayout orientation="vertical" gap={6}>
            <CPText textStyle="h6" align="center">
              Check Your Email
            </CPText>
            <CPText textStyle="subtitle" align="center">
              A 6-digit sign-in code was sent to <strong>{email}</strong>. Enter
              it below to continue.
            </CPText>
          </StackLayout>

          <StackLayout orientation="vertical" gap={8}>
            <div className="code-digit-boxes">
              {Array.from({ length: 6 }, (_, i) => (
                <input
                  key={i}
                  ref={(el) => {
                    inputRefs.current[i] = el;
                  }}
                  type="text"
                  inputMode="numeric"
                  maxLength={2}
                  value={digits[i]}
                  className={`code-digit-box${digits[i] ? " filled" : ""}`}
                  onChange={(e) => handleDigitChange(i, e.target.value)}
                  onKeyDown={(e) => handleDigitKeyDown(i, e)}
                  onPaste={handlePaste}
                  onFocus={(e) => e.target.select()}
                  autoComplete="one-time-code"
                />
              ))}
            </div>
            {codeError && (
              <CPText color="error" fontSize="13px">
                {codeError}
              </CPText>
            )}
            {resendSuccess && (
              <CPText color="success" fontSize="13px">
                A new code has been sent to your email.
              </CPText>
            )}
          </StackLayout>

          <CPButton
            themeColor="primary"
            disabled={isVerifying || code.length < 6}
            onClick={handleVerify}
          >
            {isVerifying ? "Verifying..." : "Verify Code"}
          </CPButton>

          <Box alignItems="center" justifyContent="center">
            {resendTimer > 0 ? (
              <CPText fontSize="13px" color="secondary">
                Resend code in {resendTimer}s
              </CPText>
            ) : (
              <CPButton
                fillMode="flat"
                disabled={isResending}
                onClick={handleResend}
              >
                {isResending ? "Sending..." : "Resend Code"}
              </CPButton>
            )}
          </Box>
        </StackLayout>
      </CardBody>
      <CardFooter>
        <Box alignItems="center" justifyContent="center">
          <Link
            style={{ fontSize: "14px" }}
            as="button"
            title="Cancel — return to sign-in"
            onClick={() => navigate("/")}
          />
        </Box>
      </CardFooter>
    </Card>
  );
};

export default ShortCodePage;
